// One-click "Add to CRM" button — reused from:
//   1. Admin → Schools tab actions column
//   2. Admin → Payments tab (PaymentTracker) actions column
//
// Behaviour:
//   • Pre-checks the CRM by mobile number (q=<mobile>) — if any lead exists
//     with the same mobile, we DON'T create a duplicate. Instead we surface
//     a "Already in CRM" toast with a "View CRM" link.
//   • Otherwise we POST /api/admin/crm/leads with school details pre-filled
//     (fullName / mobileNumber / whatsappNumber / schoolName / udiseCode /
//      district / block / enquiryType / leadStatus / leadSource).
//   • Toast on success has a "Open CRM" action that jumps to /admin?tab=crm.
//
// Two visual variants:
//   • variant="icon"  → small ghost icon button for dense rows (Schools tab)
//   • variant="full"  → labelled outline button (Payment Tracker actions)

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const guessEnquiryType = (lead) => {
  // PaymentTracker passes an order mode → translate to enquiry type.
  const m = (lead.paymentMode || "").toLowerCase();
  if (m === "admin") return "Admin Setup";
  if (m === "bills_uc_vouchers") return "MDM Sahayak";
  if (m === "mdm_only") return "MDM Sahayak";
  if (m.startsWith("self")) return "MDM Sahayak";
  // Schools tab passes setupChoice; fall back to a sensible default.
  return "MDM Sahayak";
};

const openCrmTab = () => {
  // AdminDashboard reads the `tab` query param only on initial mount
  // (it's wired to Tabs' defaultValue). A real navigation re-mounts the
  // page with the CRM tab active and avoids juggling cross-component state.
  window.location.href = "/admin?tab=crm";
};

export default function AddToCrmButton({
  // Required school fields (we forgive missing ones — backend only
  // mandates fullName + mobileNumber).
  schoolName,
  mobile,
  udiseCode,
  fullName,           // contact name if known — falls back to schoolName
  district,
  block,
  paymentMode,        // optional — used to pick enquiry type for orders
  notePreview,        // optional — flows into the internal admin note
  variant = "icon",   // "icon" | "full"
  testIdSuffix,       // forms data-testid="add-to-crm-<suffix>"
  className = "",
}) {
  const [busy, setBusy] = useState(false);
  const mobileClean = (mobile || "").replace(/\D/g, "");

  const handleClick = async () => {
    if (!mobileClean) {
      toast.error("No mobile number on this row — add one first.");
      return;
    }
    setBusy(true);
    try {
      // Dedupe by mobile number — relies on the existing list endpoint's
      // ?q= search (matches mobile/UDISE/name/etc.).
      const search = await axios.get(`${API}/admin/crm/leads`, {
        params: { q: mobileClean, limit: 5 },
      });
      const dup = (search.data?.rows || []).find(
        (r) => (r.mobileNumber || "").replace(/\D/g, "") === mobileClean,
      );
      if (dup) {
        toast.info(`Already in CRM — "${dup.fullName}"`, {
          action: { label: "Open CRM", onClick: openCrmTab },
        });
        return;
      }

      const body = {
        fullName: (fullName && fullName.trim()) || schoolName || "Lead",
        mobileNumber: mobileClean,
        whatsappNumber: mobileClean,
        schoolName: schoolName || "",
        udiseCode: udiseCode || "",
        district: district || "",
        block: block || "",
        enquiryType: guessEnquiryType({ paymentMode }),
        matter: notePreview || "",
        leadSource: "Direct Call",
        leadStatus: "New Lead",
        priority: "Medium Priority",
        workProgress: "Not Started",
        tags: [],
        followUpRequired: false,
        internalNote: notePreview || "",
      };
      await axios.post(`${API}/admin/crm/leads`, body);
      toast.success(`Added to CRM · ${body.fullName}`, {
        action: { label: "Open CRM", onClick: openCrmTab },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add to CRM");
    } finally {
      setBusy(false);
    }
  };

  const testId = `add-to-crm-${testIdSuffix || mobileClean || "btn"}`;

  if (variant === "full") {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={handleClick}
        disabled={busy}
        data-testid={testId}
        title="Add this school to CRM as a new lead"
        className={`h-8 gap-1 border-violet-300 text-violet-700 hover:bg-violet-50 ${className}`}
      >
        <UserPlus className="h-3.5 w-3.5" /> {busy ? "Adding…" : "Add to CRM"}
      </Button>
    );
  }

  // icon variant — fits dense Schools-tab rows
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={handleClick}
      disabled={busy}
      data-testid={testId}
      title="Add this school to CRM as a new lead"
      className={`text-violet-700 hover:text-violet-800 ${className}`}
    >
      <UserPlus className="h-3.5 w-3.5" />
    </Button>
  );
}
